/**
 * UX 1A-c — "Vajab sind": what is waiting for the WORKER, and nothing else.
 *
 * Pure and client-side, so it answers offline from what the device already
 * holds: the worker's own timesheets (`useMyTimesheetsAcrossJobs`) and the
 * device queue (`useSyncQueue`). Read by the „Täna” dashboard block, the
 * Päevad counter in WorkerShell and the top of WorkerTimesheetsPage — one
 * source, so the counter and the lists cannot disagree.
 *
 * Four kinds, in the order the worker should deal with them:
 *  1. `unfinished`   — a day still running from an earlier calendar day (the
 *                      end was forgotten). A day that started today is the
 *                      day in progress, not a task. A night shift is not
 *                      flagged while it is plausibly still going: the open
 *                      period must also be at least 12 h old.
 *  2. `failedAction` — something the device could not send and will not
 *                      retry on its own (permanently failed queue action).
 *                      Actions queued behind a refused start fold into that
 *                      start: one decision, one item.
 *  3. `rejected`     — a day sent back by the approver, still open for
 *                      resubmission (a final rejection is closed — RM24's).
 *  4. `unsubmitted`  — a closed draft day that was never sent.
 *
 * A day whose completion is already queued on this device is not listed
 * under 1, 3 or 4 — the worker has acted; the queue item speaks for it if it
 * fails. Approved, submitted and invoiced days never appear here.
 */

import {
  TIMESHEET_REJECT_REASON_CODES,
  type RejectReasonCode,
  type Timesheet,
} from '@rendimees24/shared-types';

import type { SyncQueueState } from '../services/syncQueue';

import { workerCalendarDay } from './workerTime';

export type NeedsYouKind = 'unfinished' | 'failedAction' | 'rejected' | 'unsubmitted';

export interface NeedsYouItem {
  kind: NeedsYouKind;
  /** Stable React key. */
  key: string;
  timesheetId?: string;
  jobId?: string;
  /** The day's first check-in (or the queued action's creation time). */
  at?: string;
  timeZone?: string;
  /** `rejected`: the approver's code, when the rejection has one. */
  reasonCode?: RejectReasonCode;
  /** `rejected`: the approver's free text ("kinnitaja kommentaar"). */
  reason?: string;
  /** `failedAction`: the queue action type and its error code. */
  actionType?: string;
  errorCode?: string;
  /** `failedAction`: the queued action's id. */
  actionId?: string;
}

/** The parts of the queue state this reads; every field optional (old mocks, first render). */
export type NeedsYouQueue = Partial<
  Pick<SyncQueueState, 'issues' | 'pendingCompletions' | 'pendingStarts'>
>;

export interface NeedsYouInput {
  timesheets: readonly Timesheet[] | null | undefined;
  queue?: NeedsYouQueue | null;
  now: Date;
}

const ORDER: Record<NeedsYouKind, number> = {
  unfinished: 0,
  failedAction: 1,
  rejected: 2,
  unsubmitted: 3,
};

/** An open period younger than this is not called "forgotten", even across midnight. */
export const UNFINISHED_MIN_AGE_MS = 12 * 60 * 60 * 1000;

function zoneOf(ts: Timesheet): string | undefined {
  return ts.timeZone || undefined;
}

function sameOrLater(a: string, b: string): boolean {
  return a >= b;
}

export function workerNeedsYou({ timesheets, queue, now }: NeedsYouInput): NeedsYouItem[] {
  const items: NeedsYouItem[] = [];
  const pendingCompletions = queue?.pendingCompletions ?? [];
  const queuedFinish = new Set(
    pendingCompletions.map((p) => p.timesheetId).filter((id): id is string => !!id),
  );

  for (const ts of timesheets ?? []) {
    if (!ts || queuedFinish.has(ts.id)) continue;
    const entries = ts.entries ?? [];
    const first = entries[0]?.checkInAt;
    const last = entries[entries.length - 1];
    const base = {
      key: `ts:${ts.id}`,
      timesheetId: ts.id,
      jobId: ts.jobId,
      at: first,
      timeZone: zoneOf(ts),
    };

    if (ts.status === 'draft') {
      if (!last) continue;
      if (!last.checkOutAt) {
        const openedAt = Date.parse(last.checkInAt);
        if (!Number.isFinite(openedAt)) continue;
        const startedDay = workerCalendarDay(new Date(openedAt), zoneOf(ts));
        const today = workerCalendarDay(now, zoneOf(ts));
        if (sameOrLater(startedDay, today)) continue;
        if (now.getTime() - openedAt < UNFINISHED_MIN_AGE_MS) continue;
        items.push({ ...base, kind: 'unfinished' });
      } else {
        items.push({ ...base, kind: 'unsubmitted' });
      }
      continue;
    }

    if (ts.status === 'rejected' && !ts.finalRejection) {
      // Only a code this build can name: an unknown one (a newer server) reads
      // as the approver's text alone rather than as a raw catalog key.
      const reasonCode =
        ts.rejectedReasonCode && TIMESHEET_REJECT_REASON_CODES.includes(ts.rejectedReasonCode)
          ? ts.rejectedReasonCode
          : undefined;
      items.push({
        ...base,
        kind: 'rejected',
        ...(reasonCode ? { reasonCode } : {}),
        ...(ts.rejectedReason ? { reason: ts.rejectedReason } : {}),
      });
    }
  }

  // Refused starts own the actions queued behind them.
  const refusedRefs = new Set(
    (queue?.pendingStarts ?? []).filter((s) => s.failed).map((s) => s.localRef),
  );
  for (const issue of queue?.issues ?? []) {
    if (issue.unowned) continue;
    if (issue.awaitsRef && refusedRefs.has(issue.awaitsRef)) continue;
    items.push({
      kind: 'failedAction',
      key: `q:${issue.id}`,
      actionId: issue.id,
      actionType: issue.type,
      errorCode: issue.errorCode,
      at: issue.createdAt,
    });
  }

  return items.sort(
    (a, b) => ORDER[a.kind] - ORDER[b.kind] || (b.at ?? '').localeCompare(a.at ?? ''),
  );
}
